import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	useToast,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { FiX } from "react-icons/fi";
import * as yup from "yup";

import { Button } from "newui/components/Button";
import { DateTimePickerWithLabel } from "newui/components/ui/InputWithLabel/DateTimePickerWithLabel";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";
import dayjs from "newui/utils/dayjs";
import type { EvaluationPanelMember } from "../../types/panel.type";
import type { SendInvitationsPayload } from "../../types/panel.type";
import { EvaluationInviteEmailContent } from "./EvaluationInviteEmailContent";

interface InviteConfirmationDrawerProps {
	companyName?: string;
	isOpen: boolean;
	panelMembers: EvaluationPanelMember[];
	rfxId: number;
	rfxTitle?: string;
	onClose: () => void;
	onSendInvitations: (payload: SendInvitationsPayload) => Promise<{ sent: number }>;
}

interface InviteFormValues {
	confirmed: boolean;
	deadlineDate: Date;
	deadlineTime: string;
	deadlineTimezone: string;
	message: string;
}

const validationSchema = yup.object().shape({
	deadlineDate: yup
		.date()
		.required("Deadline date is required"),
	deadlineTime: yup
		.string()
		.required("Deadline time is required"),
	message: yup
		.string()
		.max(2000, "Message must be at most 2000 characters"),
	confirmed: yup
		.boolean()
		.oneOf([true], "You must confirm before sending"),
});

const buildDeadlineISO = (date: Date, time: string, timezone: string): string => {
	const dateStr = dayjs(date).format("YYYY-MM-DD");
	return dayjs.tz(`${dateStr} ${time}`, "YYYY-MM-DD hh:mm A", timezone).toISOString();
};

const getTimezoneAbbr = (date: Date, tz: string): string => {
	try {
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone: tz,
			timeZoneName: "short",
		}).formatToParts(date);
		return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
	} catch {
		return tz;
	}
};

const formatDeadlineDisplay = (date: Date, time: string, timezone: string): string => {
	const dateStr = dayjs(date).format("YYYY-MM-DD");
	const formatted = dayjs
		.tz(`${dateStr} ${time}`, "YYYY-MM-DD hh:mm A", timezone)
		.format("MMM D, YYYY [at] h:mm A");
	const abbr = getTimezoneAbbr(date, timezone);
	return `${formatted} ${abbr}`;
};

export const InviteConfirmationDrawer: React.FC<InviteConfirmationDrawerProps> = ({
	isOpen,
	onClose,
	rfxId,
	panelMembers,
	onSendInvitations,
	companyName = "",
	rfxTitle = ""
}) => {
	const toast = useToast();
	const pendingMembers = panelMembers.filter(
		(m) => m.status === "pending"
	);

	const initialValues: InviteFormValues = {
		deadlineDate: dayjs().add(7, "days").toDate(),
		deadlineTime: "05:00 PM",
		deadlineTimezone: dayjs.tz.guess(),
		message: "",
		confirmed: false,
	};

	const handleSubmit = async (
		values: InviteFormValues,
		{ setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
	) => {
		try {
			const deadlineISO = buildDeadlineISO(
				values.deadlineDate,
				values.deadlineTime,
				values.deadlineTimezone
			);
			const result = await onSendInvitations({
				deadline: deadlineISO,
				message: values.message || undefined,
			});
			toast({
				title: "Invitations sent",
				description: `${result.sent} invitation(s) sent successfully.`,
				status: "success",
				duration: 4000,
				isClosable: true,
			});
			onClose();
		} catch {
			toast({
				title: "Error",
				description: "Failed to send invitations. Please try again.",
				status: "error",
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Drawer
			placement="right"
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent maxWidth="1280px">
				<DrawerHeader className="border-b-2">
					<Flex justifyContent="space-between" align="center">
						<div>
							<p className="text-xl font-semibold text-bg-brand-solid">
								Send Panel Invitations
							</p>
							<p className="text-sm font-normal text-gray-500">
								Review and send invitations to {pendingMembers.length} panel
								member(s). They will receive an email with instructions to
								access the evaluation.
							</p>
						</div>
						<button
							onClick={onClose}
							className="rounded-lg p-2 hover:bg-gray-100"
							aria-label="Close drawer"
							data-testid="close-invite-drawer"
						>
							<FiX size={20} />
						</button>
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
						validateOnMount={false}
					>
						{({ isSubmitting, isValid, values, setFieldValue }) => (
							<Form>
								<div className="grid grid-cols-3">
									<div className="col-span-1 pr-6">
										<p className="mb-4 text-lg font-semibold text-primary-900">
											Invitation Details
										</p>

										<div className="mb-4">
											<DateTimePickerWithLabel
												label="Deadline"
												dateFieldName="deadlineDate"
												timeFieldName="deadlineTime"
												timeZoneFieldName="deadlineTimezone"
												defaultDate={values.deadlineDate}
												defaultDaysFromToday={7}
												defaultTime={values.deadlineTime}
												required
												onValueChanged={(date, time, tz) => {
													setFieldValue("deadlineDate", date);
													setFieldValue("deadlineTime", time);
													setFieldValue("deadlineTimezone", tz);
												}}
											/>
										</div>

										<div className="mb-4">
											<TextAreaWithLabel
												label="Additional Message (optional)"
												placeholder="Add a personal message to the invitation..."
												name="message"
												value={values.message}
												onChange={(e) => setFieldValue("message", e.target.value)}
												maxLength={2000}
											/>
										</div>

										<div className="mb-6">
											<label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
												<input
													type="checkbox"
													checked={values.confirmed}
													onChange={(e) =>
														setFieldValue("confirmed", e.target.checked)
													}
													className="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer"
													data-testid="confirm-checkbox"
												/>
												<span>
													I confirm the email addresses above are correct and
													understand that by sending these invitations, the
													panel members listed will be granted access to view
													supplier responses for this RFx.
												</span>
											</label>
										</div>

										<Flex gap={3} justify="flex-end">
											<Button
												btntype="secondary"
												onClick={onClose}
												disabled={isSubmitting}
												data-testid="cancel-invite-btn"
											>
												Cancel
											</Button>
											<Button
												type="submit"
												disabled={
													isSubmitting || !isValid || !values.confirmed
												}
												isLoading={isSubmitting}
												data-testid="send-invitations-btn"
											>
												Send Invitations
											</Button>
										</Flex>
									</div>
									<div className="col-span-2">
										<p className="ml-10 px-11 text-lg font-semibold text-primary-900">
											Email Preview
										</p>
										<div className="ml-10 px-11">
											<EvaluationInviteEmailContent
												companyName={companyName}
												rfxTitle={rfxTitle}
												deadline={formatDeadlineDisplay(
													values.deadlineDate,
													values.deadlineTime,
													values.deadlineTimezone
												)}
												additionalMessage={values.message}
											/>
										</div>
									</div>
								</div>
							</Form>
						)}
					</Formik>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
